import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const projectKeys = {
  all: ['projects'],
  lists: () => [...projectKeys.all, 'list'],
  details: () => [...projectKeys.all, 'detail'],
  detail: (id) => [...projectKeys.details(), id],
  bySubscriber: (subscriberId) => [...projectKeys.all, 'subscriber', subscriberId],
};

/**
 * Hook para obtener todos los proyectos
 */
export const useProjects = (options = {}) => {
  return useQuery({
    queryKey: projectKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('projects');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener un proyecto específico
 * @param {string} id - UUID del proyecto
 * @param {Object} options - Opciones adicionales para la query
 */
export const useProject = (id, options = {}) => {
  return useQuery({
    queryKey: projectKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`projects/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener proyectos por suscriptor
 * @param {string} subscriberId - UUID del suscriptor
 * @param {Object} options - Opciones adicionales para la query
 */
export const useProjectsBySubscriber = (subscriberId, options = {}) => {
  return useQuery({
    queryKey: projectKeys.bySubscriber(subscriberId),
    queryFn: async () => {
      const response = await apiClient.get(`projects/by-subscriber/${subscriberId}`);
      return response.data;
    },
    enabled: !!subscriberId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para crear un nuevo proyecto
 */
export const useCreateProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectData) => {
      const response = await apiClient.post('projects', projectData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['lots'] });
    },
    onError: (error) => {
      console.error('Error creating project:', error);
    },
  });
};

/**
 * Hook para actualizar un proyecto
 */
export const useUpdateProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`projects/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(projectKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['lots'] });
    },
    onError: (error) => {
      console.error('Error updating project:', error);
    },
  });
};

/**
 * Hook para eliminar un proyecto
 */
export const useDeleteProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`projects/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.removeQueries({ queryKey: projectKeys.detail(deletedId) });
      queryClient.invalidateQueries({ queryKey: ['lots'] });
    },
    onError: (error) => {
      console.error('Error deleting project:', error);
    },
  });
};

/**
 * Hook para actualizar el estado de un proyecto
 */
export const useUpdateProjectStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }) => {
      const response = await apiClient.put(`projects/${id}/status`, { status });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(projectKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating project status:', error);
    },
  });
};

/**
 * Hook para asignar templates a un proyecto
 */
export const useAssignTemplatesToProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, templateUuids }) => {
      const response = await apiClient.post(`project_task_assignments/bulk-assign/${id}`, {
        template_uuids: templateUuids
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: ['lots'] });
      queryClient.invalidateQueries({ queryKey: ['projectTaskAssignments'] });
      queryClient.invalidateQueries({ queryKey: ['projectProgressTracking'] });
      queryClient.invalidateQueries({ queryKey: ['projectQualityTracking'] });
      queryClient.invalidateQueries({ queryKey: ['projectProgressTrackingHistory'] });
    },
    onError: (error) => {
      console.error('Error assigning templates to project:', error);
    },
  });
};
